import { z } from "zod";

import type { components } from "@/api/schema";

const MARKETPLACE = ["shopee", "lazada", "tiktok"] as const;

/**
 * CreatePromotionRequest — the BE two-step shell. POST /v1/promotions creates the
 * promotion (basics + targeting + rule_mode, NO rules); rules land later via the
 * builder's PUT upsert. Mirrors components["schemas"]["CreatePromotionRequest"];
 * one schema = validation + inferred type (CLAUDE.md), no parallel hand-written
 * type. Reuses the F1 promotionSchema's field rules for the shell subset.
 */
export const createPromotionSchema = z
  .object({
    campaign_id: z.string().trim().min(1, "promotion:create.err_campaign_required"),
    name: z.string().trim().min(1, "promotion:builder.err_name_required"),
    marketplace: z.enum(MARKETPLACE),
    seller_id: z.string().trim().min(1, "promotion:builder.err_seller_required"),
    start_at: z.string().min(1, "promotion:create.err_start_required"),
    end_at: z.string().min(1, "promotion:create.err_end_required"),
    rule_mode: z.enum(["all", "break"]),
  })
  .superRefine((promo, ctx) => {
    if (Date.parse(promo.end_at) <= Date.parse(promo.start_at)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["end_at"],
        message: "promotion:builder.err_dates",
      });
    }
  });

export type CreatePromotionFormValues = z.infer<typeof createPromotionSchema>;
export type CreatePromotionFormInput = z.input<typeof createPromotionSchema>;

/**
 * Runtime/compile bridge: the form output must map onto the BE create body. We
 * model the required keys explicitly rather than `extends` because
 * `exactOptionalPropertyTypes` makes a structural `extends` over optional fields
 * brittle (mirrors promotion-schema's UpsertBody guard).
 */
type CreateBody = components["schemas"]["CreatePromotionRequest"];
type _AssertKeys = keyof CreatePromotionFormValues extends keyof CreateBody ? true : never;
const _keys: _AssertKeys = true;
void _keys;
